import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';

import api from '@js/actions/api';

import MapInterface from '@js/modules/maps/mapInterface';

// import PWAManager from '@js/modules/pwaManager';

import Polyline from '@js/modules/maps/polyline';

import {DefaultRideType, RideTypes} from '@js/components/modules/constants';


export default class RideMap extends MapInterface {
    _mapModel = {
        // Ride properties
        rideId: null,
        rideShareUrl: null,
        ridePoisCount: 0,

        ridesData: [],
        hasRideData: true,

        // Group Ride layers
        rideFeatures: [],

        // Trigger when a marker is clicked
        markerClicked: [],

        // Minimum zoom to show direction arrows
        minZoomDirectionArrow: 13,
        // Direction arrows for the main track
        directionArrows: [],

        slopePercentageEnabled: false,
        surfaceTypeEnabled: false
    };

    constructor(modules, params) {
        super(modules, params);

        this._mapModel.rideId = params.rideId;
        this._mapModel.rideShareUrl = params.rideShareUrl;
        this._mapModel.ridePoisCount = params.ridePoisCount;

        this.onMapLoaded(() => {
            this._fetchRideData();

            this._model.map.on('zoomend', () => {
                if (this.isDataChangedOnZoom(this._model.map.getZoom(), this._model.previousZoom, true)) {
                    this._drawDirectionArrows();
                }
            });

            this._initializeMarkers();
        });
    }

    toggleSlopePercentage = (enabled, coordinateElevations) => {
        this._mapModel.slopePercentageEnabled = enabled;

        const trackSourceId = this._getMainTrackSourceId();
        if (!trackSourceId) {
            return;
        }

        if (enabled) {
            // Slope and surface both recolour the same track: only one at a time.
            if (this._mapModel.surfaceTypeEnabled) {
                this._mapModel.surfaceTypeEnabled = false;
                this.setSurfaceTypeMode(trackSourceId, false);
            }

            if (coordinateElevations?.length) {
                const slopeGeoJSON = this.buildSlopeGeoJSON(coordinateElevations);
                this.updateSource(trackSourceId, slopeGeoJSON);
                this.setSlopePercentageMode(trackSourceId, true);
            }
        } else {
            this.setSlopePercentageMode(trackSourceId, false);
            this._redrawTracks();
        }
    };

    // Whether the main track carries persisted surface ranges (drives the surface toggle visibility).
    hasSurfaceData = () => {
        return !!this._mapModel.rideFeatures[0]?.surfaces?.length;
    };

    toggleSurfaceType = (enabled) => {
        this._mapModel.surfaceTypeEnabled = enabled;

        const trackFeature = this._mapModel.rideFeatures[0];
        const trackSourceId = trackFeature?.polylineSourceId;
        if (!trackSourceId) {
            return;
        }

        if (enabled) {
            if (this._mapModel.slopePercentageEnabled) {
                this._mapModel.slopePercentageEnabled = false;
                this.setSlopePercentageMode(trackSourceId, false);
            }

            this._applyRideSurfacesOnce(trackFeature);
            const surfaceGeoJSON = this.buildSurfaceGeoJSON(trackFeature.linePoints);
            this.setSurfaceTypeMode(trackSourceId, true, surfaceGeoJSON);
        } else {
            this.setSurfaceTypeMode(trackSourceId, false);
            this._redrawTracks();
        }
    };

    // Distance share per surface group along the main track: [{group, distance, percent}] sorted
    // desc. Mirrors buildMap.getSurfaceBreakdown but reads the ride-show track (rideFeatures[0]).
    getSurfaceBreakdown = () => {
        const trackFeature = this._mapModel.rideFeatures[0];
        if (!trackFeature?.linePoints?.length) {
            return [];
        }

        this._applyRideSurfacesOnce(trackFeature);

        const linePoints = trackFeature.linePoints;
        const totals = {};
        let total = 0;

        for (let i = 1; i < linePoints.length; i++) {
            const curr = linePoints[i];
            const prev = linePoints[i - 1];
            const distance = curr.deltaDistance || this.distHaversine(prev.latLng, curr.latLng, MapInterface.COORDINATES_PRECISION);
            const group = MapInterface.getSurfaceGroup(curr.surface || prev.surface);

            totals[group] = (totals[group] || 0) + distance;
            total += distance;
        }

        if (!total) {
            return [];
        }

        return Object.keys(totals)
            .map((group) => ({
                group,
                distance: totals[group],
                percent: Math.round(totals[group] / total * 100)
            }))
            .sort((a, b) => b.distance - a.distance);
    };

    // Restore persisted surface GROUPS (payload ranges [{from, to, surface}]) onto the decoded
    // linePoints, once per track, mapping each group back to a representative raw surface so the
    // shared buildSurfaceGeoJSON/getSurfaceGroup logic works unchanged.
    _applyRideSurfacesOnce = (trackFeature) => {
        if (!trackFeature || trackFeature._surfacesApplied || !trackFeature.surfaces?.length || !trackFeature.linePoints?.length) {
            return;
        }

        const groupToSurface = (group) => {
            const config = MapInterface.SURFACE_GROUPS[group];
            return (config && config.surfaces[0]) || '';
        };

        trackFeature.surfaces.forEach((range) => {
            const surface = groupToSurface(range.surface);
            for (let i = range.from; i <= range.to && i < trackFeature.linePoints.length; i++) {
                if (trackFeature.linePoints[i]) {
                    trackFeature.linePoints[i].surface = surface;
                }
            }
        });

        trackFeature._surfacesApplied = true;
    };

    _getMainTrackSourceId = () => {
        return this._mapModel.rideFeatures[0]?.polylineSourceId || null;
    };

    // onMarkerClicked = (markerCallback) => {
    //     this._mapModel.markerClicked.push(markerCallback);
    // };

    /* Private Methods
    ******************** */
    _initializeMarkers = () => {
        RideTypes
            .forEach((rideType) => {
                this.manageMarkerPopup(
                    rideType + '-primary-marker',
                    // Click
                    (markerCoordinates, markerProperties) => {
                        const markerFeatureIndex = markerProperties.markerFeatureIndex;

                        this._model.hasPopup = true;

                        this._mapModel.rideFeatures[markerFeatureIndex].startMarkerPopup
                            .addTo(this._model.map);

                        this._model.openPopups.push(this._mapModel.rideFeatures[markerFeatureIndex].startMarkerPopup);

                        this._markerClickedFuncs('ride', this._mapModel.rideFeatures[markerFeatureIndex]);
                    }
                );
            });
    };

    _fetchRideData = () => {
        this._fetchRideTrack(() => {
            if (this._mapModel.ridePoisCount && this.hasModule('RIDE_POIS') && this.fetchRidePOIs) {
                setTimeout(() => {
                    this.fetchRidePOIs(this._mapModel.rideId);
                }, 200);
            }

            this._fitMapBounds();
        });
    };

    _fetchRideTrack = (doneCallback) => {
        // Check if already downloaded
        const currentRideFeature = this._mapModel.rideFeatures.find((rideFeature) => rideFeature.rideId === this._mapModel.rideId);
        if (currentRideFeature && currentRideFeature.linePoints.length > 2) {
            this._mapModel.hasRideData = true;

            // Draw data
            this._drawTracksAndMarkers();

            if (typeof doneCallback === 'function') {
                doneCallback();
            }

            return;
        }

        this._mapModel.hasRideData = false;

        const requestParam = {
            share: this._mapModel.rideShareUrl,
            filter: {
                rideId: this._mapModel.rideId,
            }
        };

        api.get('/api/v1/rides/complete-tracks', requestParam)
            .then((ridesData) => {
                if (this._model.isDestroyed || !ridesData || !ridesData.rides) {
                    return;
                }

                this._mapModel.updatedRideIds = [];
                ridesData.rides.forEach((newRide) => {
                    this._mapModel.hasRideData = true;

                    this._mapModel.ridesData = this._mapModel.ridesData.replaceOrAdd('rideId', newRide);
                });

                // Draw data
                this._drawTracksAndMarkers();

                if (typeof doneCallback === 'function') {
                    doneCallback();
                }
            });
    };

    _drawTracksAndMarkers = () => {
        // establish key points to edit with
        this._setupTracks();

        // draw markers
        this._redrawMarkers();

        // draw trip polyline, add to map
        this._redrawTracks();

        this._drawDirectionArrows();
    };

    _setupTracks = () => {
        if (Utils.isEmpty(this._mapModel.ridesData)) {
            // Empty set of tracks
            return;
        }

        let lineTracksPoints;
        let newEncodedPolyline;

        this._mapModel.loadedRideIds = [];
        this._mapModel.rideFeatures = [];

        for (let trackIndex = 0; trackIndex < this._mapModel.ridesData.length; trackIndex++) {
            const currentRideTrack = this._mapModel.ridesData[trackIndex];

            if (Utils.isEmpty(currentRideTrack)) {
                continue;
            }

            const currentTrackFeature = {
                ...currentRideTrack,
                rideId: currentRideTrack.rideId || currentRideTrack.id,
                defaultPicture: currentRideTrack.defaultPicture?.mini?.jpg,
                linkName: I18n.t('js.maps.marker.ride.details'),
                rideType: currentRideTrack.rideType.toLowerCase(),
            };

            currentTrackFeature.linePoints = [];
            currentTrackFeature.polylineSourceId = this.addMapTrackLine(this._model.mapTrackLineName + '-' + currentTrackFeature.rideId);

            newEncodedPolyline = currentRideTrack.encodedPolyline || currentRideTrack.simplifiedPolyline;

            if (Utils.isPresent(newEncodedPolyline)) {
                // Decode polyline to convert in [lat, lng] format
                const trackPolylineDecoded = Polyline.decode(newEncodedPolyline);
                lineTracksPoints = [];
                let deltaDistancePolylineDecoded;
                if (Utils.isPresent(currentRideTrack.encodedDeltaDistance)) {
                    deltaDistancePolylineDecoded = Polyline.decode(currentRideTrack.encodedDeltaDistance).flat();
                }
                for (let trackCoord = 0; trackCoord < trackPolylineDecoded.length; trackCoord++) {
                    lineTracksPoints.push(
                        new MapInterface.PointInfo(
                            {
                                lat: trackPolylineDecoded[trackCoord][0],
                                lng: trackPolylineDecoded[trackCoord][1]
                            },
                            null,
                            {
                                deltaDistance: deltaDistancePolylineDecoded[trackCoord] || null,
                                // computed: true,
                                // deltaTime: deltaTime,
                                // deltaPause: deltaPause,
                                // datetime: datetime,
                                // elevation: elevation,
                                // speed: speed,
                                // heartRate: heartRate
                            }
                        )
                    );
                }

                if (lineTracksPoints.length > 0) {
                    currentTrackFeature.linePoints = lineTracksPoints;
                }
            }

            if (currentTrackFeature.linePoints[0]) {
                currentTrackFeature.startMarkerPopup = this.buildPopup()
                    .setLngLat(currentTrackFeature.linePoints[0].latLng)
                    .setHTML(this._buildStartMarkerPopup(currentTrackFeature));
            }

            this._mapModel.rideFeatures.push(currentTrackFeature);
        }
    };

    _buildStartMarkerPopup = (rideFeature) => {
        // let rideId = rideFeature.rideId;
        const rideName = rideFeature.name;
        const rideLinkUrl = rideFeature.link;
        // let rideLinkName = rideFeature.linkName;
        const rideType = rideFeature.rideType;
        // let rideDistance = rideFeature.distance;
        const rideFormattedDistance = rideFeature.formattedDistance;
        // let rideDuration = rideFeature.duration;
        const rideFormattedDuration = rideFeature.formattedDuration;
        const rideLevel = rideFeature.level;
        const defaultPicture = rideFeature.defaultPicture;

        // let needToUpdate = (this._mapModel.loadedRideIds.indexOf(rideId) === -1 && this._mapModel.updatedRideIds.indexOf(rideId) === -1) || this._mapModel.redrawAllTracksAndMarkers;
        // let correctModeAndType = this._model.type === globalMap.type.SELECTED ||
        //     this._model.mode === globalMap.mode.EXPLORE ||
        //     this._model.mode === globalMap.mode.CHOROPLETH ||
        //     this._model.mode === globalMap.mode.ASSOCIATION;
        // if (!needToUpdate || !correctModeAndType) {
        //     continue;
        // }

        // Link inside link is not authorized
        const popupContentElement = document.createElement('a');
        popupContentElement.href = rideLinkUrl;
        // Not working on Safari
        // if (PWAManager.getPWADisplayMode() === PWAManager.MODE.BROWSER) {
        //     popupContentElement.target = '_blank';
        // }
        popupContentElement.className = 'popup-info-box';

        if (!!defaultPicture) {
            const imageContainerElement = document.createElement('div');
            imageContainerElement.className = 'image';
            const imageContentElement = document.createElement('img');
            imageContentElement.src = defaultPicture;
            imageContainerElement.appendChild(imageContentElement);
            popupContentElement.appendChild(imageContainerElement);
        }

        const popupDetailsElement = document.createElement('div');
        popupDetailsElement.className = 'popup-details';

        const titleElement = document.createElement('h3');
        titleElement.className = 'popup-title';
        // User content: text, never HTML
        titleElement.textContent = rideName;
        popupDetailsElement.appendChild(titleElement);

        const popupInfoElement = document.createElement('div');
        popupInfoElement.className = 'popup-info';

        const popupInfoMetricsElement = document.createElement('div');
        popupInfoMetricsElement.className = 'popup-info-metrics';
        // popupInfoMetricsElement.innerHTML = '<i class="material-icons">keyboard_tab</i><span>' + rideFormattedDistance + '</span><span class="popup-info-separator"></span><i class="material-icons">schedule</i><span>' + rideFormattedDuration + '</span>';
        popupInfoMetricsElement.innerHTML = '<span>' + rideFormattedDistance + '</span><span class="popup-info-separator"></span><span>' + rideFormattedDuration + '</span>';

        popupInfoElement.appendChild(popupInfoMetricsElement);

        const popupInfoTypeElement = document.createElement('div');
        popupInfoTypeElement.className = 'popup-info-details';
        popupInfoTypeElement.innerHTML = '<span class="popup-info-type">' + I18n.t('js.ride.enums.ride_type')[rideType] + '</span>';
        if (rideLevel !== 'level_undefined') {
            popupInfoTypeElement.innerHTML += '<span class="popup-info-level">' + I18n.t('js.ride.enums.level')[rideLevel] + '</span>';
        }
        popupInfoElement.appendChild(popupInfoTypeElement);

        popupDetailsElement.appendChild(popupInfoElement);

        popupContentElement.appendChild(popupDetailsElement);

        const popupContainerElement = document.createElement('div');
        popupContainerElement.className = rideType + '-popup'; // running-popup / hiking-popup / walk-popup / bike-popup
        popupContainerElement.appendChild(popupContentElement);

        return popupContainerElement.outerHTML;
    };

    _markerClickedFuncs = (type, markerProperties) => {
        for (let i = 0, n = this._mapModel.markerClicked.length; i < n; i++) {
            if (markerProperties) {
                this._mapModel.markerClicked[i]({
                    type,
                    id: markerProperties.id || markerProperties.rideId,
                    name: markerProperties.name,
                    link: markerProperties.link
                });
            } else {
                this._mapModel.markerClicked[i](null);
            }
        }
    };

    _redrawMarkers = () => {
        let linePoints;
        let rideType;
        let startLinePoint;
        let currentRideId;
        const mapMarkers = {};

        for (let trackIndex = 0; trackIndex < this._mapModel.rideFeatures.length; trackIndex++) {
            linePoints = this._mapModel.rideFeatures[trackIndex].linePoints;

            if (linePoints.length > 0) {
                startLinePoint = linePoints[0];
                currentRideId = this._mapModel.rideFeatures[trackIndex].rideId;

                rideType = this._mapModel.rideFeatures[trackIndex].rideType || DefaultRideType;
                startLinePoint.keyPoint = {
                    type: 'Feature',
                    properties: {
                        rideId: currentRideId,
                        markerFeatureIndex: trackIndex
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [startLinePoint.latLng.lng, startLinePoint.latLng.lat]
                    }
                };
                mapMarkers[rideType] = (mapMarkers[rideType] || []).concat(startLinePoint.keyPoint);
            }
        }

        Object.keys(mapMarkers)
            .forEach((type) => {
                this.updateSource(type + '-primary-marker', {
                    type: 'FeatureCollection',
                    features: mapMarkers[type]
                });
            });
    };

    _redrawTracks = () => {
        if (!this._model.map) {
            return;
        }

        let linePoints;

        // if (this._model.currentHighlightedRide && this._model.highlightedRideOut) {
        //     this._model.highlightedRideOut = false;
        //     return;
        // }

        this._model.pointsByDistanceTraveled = {};

        for (let trackIndex = 0; trackIndex < this._mapModel.rideFeatures.length; trackIndex++) {
            if (this._mapModel.currentHighlightedRide && this._mapModel.currentHighlightedRide.rideId !== this._mapModel.rideFeatures[trackIndex].rideId) {
                continue;
            }

            // if (
            //     this._model.loadedRideIds.indexOf(this._mapModel.rideFeatures[trackIndex].rideId) === -1 ||
            //     !this._mapModel.rideFeatures[trackIndex].polylineSourceId ||
            //     this._mapModel.rideFeatures[trackIndex].linePoints.length === 2
            // ) {
            linePoints = this._mapModel.rideFeatures[trackIndex].linePoints;

            if (linePoints.length < 3) {
                continue;
            }

            let totalDistance = 0;

            let thisLegDist;

            // draw this line segment
            const nextLine = [];
            for (let pointIndex = 0; pointIndex <= linePoints.length - 1; pointIndex++) {
                nextLine.push(linePoints[pointIndex].latLng);

                const thisLinePointLatLng = linePoints[pointIndex].latLng;
                let prevLinePointLatLng;

                if (pointIndex !== 0) {
                    prevLinePointLatLng = linePoints[pointIndex - 1].latLng;
                    thisLegDist = linePoints[pointIndex].deltaDistance || this.distHaversine(prevLinePointLatLng, thisLinePointLatLng, MapInterface.COORDINATES_PRECISION);
                    totalDistance = Utils.flooredNum(totalDistance + thisLegDist, MapInterface.COORDINATES_PRECISION);
                }

                this._model.pointsByDistanceTraveled[Math.floor(totalDistance)] = thisLinePointLatLng;

                // Are we 1 distance unit (or more) greater? try to draw a distance marker
                // let curDist = traveledDistance / 1000;
                // if (curDist >= markerCount + 1) {
                //     let markersToAdd = Math.floor(curDist) - markerCount;
                //     for (let k = 0; k < markersToAdd; k++) {
                //         // kick marker number
                //         markerCount++;
                //
                //         // if zoomed in far enough, show all individual mile markers
                //         // if not zoomed in past 13, show every 5 if at 12
                //         // if not zoomed in past 12, show every 10 if at 11
                //         if ((this._model.map.getZoom() >= 13) ||
                //             ((this._model.map.getZoom() >= 12) && (markerCount % 5 === 0)) ||
                //             ((this._model.map.getZoom() >= 11) && markerCount % 10 === 0)) {
                //             // find lat lng
                //             let distanceToDistMarker = markerCount - curDist;
                //             let percentageDist;
                //             if (this._model.distanceUnits === 'mi') {
                //                 percentageDist = distanceToDistMarker / (thisLegDist / 1000 / 1.609344);
                //             } else {
                //                 percentageDist = distanceToDistMarker / (thisLegDist / 1000);
                //             }
                //         }
                //     }
                // }
            }

            // draw lines on map in the set color
            const thisLine = {
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: nextLine.map(function (lngLat) {
                        return [lngLat.lng, lngLat.lat];
                    })
                }
            };
            this.updateSource(this._mapModel.rideFeatures[trackIndex].polylineSourceId, thisLine);

            const sections = this._mapModel.rideFeatures[trackIndex].sections;
            if (sections && sections.length) {
                const sectionGeoJSON = this.buildSectionColorGeoJSON(linePoints, sections);
                this.setSectionColorMode(this._mapModel.rideFeatures[trackIndex].polylineSourceId, true, sectionGeoJSON);
            }

            // Add end marker
            const endLinePoint = linePoints[linePoints.length - 1];
            endLinePoint.keyPoint = this.updateSource('finish-marker', {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [endLinePoint.latLng.lng, endLinePoint.latLng.lat]
                        }
                    }
                ]
            });

            // // set up event handlers but not for updated rides
            // if (this._mapModel.updatedRideIds.indexOf(this._mapModel.rideFeatures[trackIndex].rideId) === -1) {
            //     this._addMarkersPointEventHandlers('ride', this._mapModel.rideFeatures[trackIndex]);
            // }

            // // Instead, show polyline on click
            // this._mapModel.rideFeatures[trackIndex].startMarker.on('click', L.bind(this._markerOnClick.bind(this), null, this._mapModel.rideFeatures[trackIndex].polylineLayer));
            // }
        }
    };

    _removeDirectionArrows = () => {
        if (!this._mapModel.directionArrows.length) {
            return;
        }

        this.resetMapSource('arrow-marker');

        this._mapModel.directionArrows = [];
    };

    _drawDirectionArrows = () => {
        this._removeDirectionArrows();

        if (this._model.map.getZoom() < this._mapModel.minZoomDirectionArrow || this._mapModel.rideFeatures.length === 0) {
            return;
        }

        // how often SHOULD we be placing direction arrows?
        const directionArrowInterval = this.getZoomDictionary(this._model.map.getZoom()).directionArrow;
        if (!directionArrowInterval) {
            return;
        }

        if (this._mapModel.rideFeatures.length === 1) {
            const currentTrack = this._mapModel.rideFeatures[0].linePoints;
            if (currentTrack.length < 3) {
                return;
            }

            // draw a fake point in between every key point to allow user to insert a new point here.
            // variable for distance since last direction arrow
            let distanceSinceLastDirectionArrow = 0;

            // after placing a direction arrow, how much distance is left over (so we can place correctly after the next invisible point)
            let carryoverDistance = 0;
            let lastKeyPointIndex = 0;
            for (let linePointIndex = 0; linePointIndex < currentTrack.length; linePointIndex++) {
                let prevLinePoint;
                const thisLinePoint = currentTrack[linePointIndex];

                // variable for point-to-point distance
                let distanceBetween = 0;
                if (linePointIndex > 0) {
                    prevLinePoint = currentTrack[linePointIndex - 1];
                    distanceBetween = this.distHaversine(prevLinePoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                    distanceSinceLastDirectionArrow += distanceBetween;
                }

                // if we've traveled more than the direction arrow interval, find out how many to place
                let directionArrowsToPlace = 0;
                if (distanceSinceLastDirectionArrow > directionArrowInterval) {
                    directionArrowsToPlace = Math.floor(distanceSinceLastDirectionArrow / directionArrowInterval);
                }

                // start with carryover distance
                // move from point A to point B, placing direction arrows at interval specified (remove carryover from last loop iteration)
                // when done, set carryover distance and continue
                // guard against zero-length segments (duplicate consecutive points):
                // dividing by distanceBetween would yield Infinity/NaN coordinates
                if (directionArrowsToPlace > 0 && distanceBetween > 0) {
                    for (let directionArrowIndex = 1; directionArrowIndex <= directionArrowsToPlace; directionArrowIndex++) {
                        const distanceToNextDirectionArrow = (directionArrowInterval * directionArrowIndex) - carryoverDistance;
                        const percentageBetweenPoints = distanceToNextDirectionArrow / distanceBetween;

                        // we now know at what percentage distance the direction arrow should be placed. Use to put it on the line
                        const directionArrowLat = prevLinePoint.latLng.lat + ((thisLinePoint.latLng.lat - prevLinePoint.latLng.lat) * percentageBetweenPoints);
                        const directionArrowLng = prevLinePoint.latLng.lng + ((thisLinePoint.latLng.lng - prevLinePoint.latLng.lng) * percentageBetweenPoints);

                        const diffLat = thisLinePoint.latLng.lat - prevLinePoint.latLng.lat;
                        const diffLng = thisLinePoint.latLng.lng - prevLinePoint.latLng.lng;
                        // let center = [thisLinePoint.latLng.lat + diffLat / 2, thisLinePoint.latLng.lng + diffLng / 2];
                        const angle = (360 - ((Math.atan2(diffLat, diffLng) * 57.295779513082) % 360));

                        // create direction arrow object
                        const newDirectionArrow = new MapInterface.DirectionArrow(
                            {
                                type: 'Feature',
                                properties: {
                                    angle: angle,
                                    linePointIndex: linePointIndex,
                                    directionArrowIndex: directionArrowIndex
                                },
                                geometry: {
                                    type: 'Point',
                                    coordinates: [directionArrowLng, directionArrowLat]
                                }
                            },
                            lastKeyPointIndex
                        );

                        // add to array for reference
                        this._mapModel.directionArrows.push(newDirectionArrow);

                        if (directionArrowIndex === directionArrowsToPlace) {
                            // last need
                            carryoverDistance = distanceBetween - (percentageBetweenPoints * distanceBetween);
                            distanceSinceLastDirectionArrow = carryoverDistance;
                        }
                    }
                } else {
                    carryoverDistance += distanceBetween;
                }

                if (thisLinePoint.keyPoint !== null) {
                    lastKeyPointIndex = linePointIndex;
                }
            }

            this.updateSource('arrow-marker', {
                type: 'FeatureCollection',
                features: this._mapModel.directionArrows
                    .map(function (directionArrow) {
                        return directionArrow.keyPoint;
                    })
                    // safety net: never hand non-finite coordinates to MapLibre
                    .filter(function (feature) {
                        const coords = feature && feature.geometry && feature.geometry.coordinates;
                        return coords && Number.isFinite(coords[0]) && Number.isFinite(coords[1]);
                    })
            });
        }
    };

    _fitMapBounds = () => {
        // get the map bounds by looking at all polylines involved
        if (this._model.mapBounds !== null) {
            return;
        }

        let bounds = this._buildBounds();

        // Fit to polylines or clusters
        if (this._mapModel.rideFeatures.length > 0) {
            for (let rideIndex = 0; rideIndex < this._mapModel.rideFeatures.length; rideIndex++) {
                this._mapModel.rideFeatures[rideIndex].linePoints.forEach((point) => {
                    bounds = bounds.extend([point.latLng.lng, point.latLng.lat]);
                });
            }
        }

        if (this.hasModule('RIDE_POIS')) {
            if (this._mapModel.poisData) {
                Object.keys(this._mapModel.poiFeatures)
                    .forEach((rideId) => {
                        for (let i = 0; i < this._mapModel.poiFeatures[rideId].markerInfos.length; i++) {
                            const markerInfo = this._mapModel.poiFeatures[rideId].markerInfos[i];
                            bounds = bounds.extend([markerInfo.latLng.lng, markerInfo.latLng.lat]);
                        }
                    });
            }
        }

        if (Utils.isPresent(this._model.includeBounds) && this._model.includeBounds.length === 4) {
            const bbox = [
                [
                    parseFloat(this._model.includeBounds[1]),
                    parseFloat(this._model.includeBounds[0])
                ],
                [
                    parseFloat(this._model.includeBounds[3]),
                    parseFloat(this._model.includeBounds[2])
                ]
            ];
            bounds = bounds.extend(bbox);
        }

        // fit to the overall bounds
        const fitBoundsOptions = {
            padding: 100
        };

        this._model.mapBounds = bounds;
        this.fitBounds(this._model.mapBounds, fitBoundsOptions);
    };

    // Called by MapInterface
    _onMapStyleChanged = () => {
        for (let trackIndex = 0; trackIndex < this._mapModel.ridesData.length; trackIndex++) {
            if (Utils.isEmpty(this._mapModel.ridesData[trackIndex])) {
                continue;
            }

            this.addMapTrackLine(this.mapTrackLineName + '-' + this._mapModel.ridesData[trackIndex].id);
        }

        // Draw data
        this._drawTracksAndMarkers();

        if (this.hasModule('RIDE_POIS') && this.clearRidePoiMarkersPoints && this.fetchRidePOIs) {
            this.clearRidePoiMarkersPoints();

            this.fetchRidePOIs(this._mapModel.rideId);
        }
    };
}
